// src/pages/Search.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ENV, tbGetJson, hingeGetJson, buildUrl } from "../api/client";
import { formatDateMMDDYYYY } from "../utils/dateFormat";
import { isValidSearchQuery } from "../utils/searchValidation";
import { isLookupReady, useLookupReady } from "../hooks/useLookupReady";
import { toast } from "react-toastify";
import "./Search.css";

const CACHE_KEY = "npw.search.cache.v1";
const RETURN_PATH = "npw.search.returnPath"; // compatibility
const LAST_PAGE_KEY = "npw.search.lastPage";
const FROM_SEARCH = "npw.search.fromSearch";

// used by host page logic
const REOPEN_SEARCH = "npw.search.reopen";
const HOST_PATH = "npw.search.host";

const PAGE_SIZE = 10;

/* ===================== ENV ===================== */
const TB_SEARCH_URL = ENV.TB_SEARCH_URL;

// Feature flag can remain as-is; now the “hinge search” happens via TeamBridge API.
const ENABLE_HINGE = ENV.ENABLE_HINGE;

// ✅ TeamBridge “Hinge Search” API (proxy) URL from client.js ENV
const TB_HINGE_SEARCH_URL =
    ENV.TB_HINGE_SEARCH_URL ||
    ENV.TB_HINGE_PERSON_SEARCH_URL ||
    ENV.TB_HINGE_SEARCH_PROXY_URL ||
    "";

// Keep same min-length behavior
const HINGE_MIN = Number(ENV.HINGE_MIN || 4);

const MIN_SEARCH_CHARS = 4;
const GLOBAL_TOAST_CONTAINER_ID = "global";

/* ---------- cache & session ---------- */
function readCache() {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}
function readLastPageOnce() {
    try {
        const v = sessionStorage.getItem(LAST_PAGE_KEY);
        if (v != null) sessionStorage.removeItem(LAST_PAGE_KEY);
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}

const norm = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();

const enUsDate = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
});

function formatMmddyyyy(value) {
    const s = norm(value);
    if (!s) return "";

    // Prefer shared helper for common API shapes.
    const viaHelper = formatDateMMDDYYYY(s);
    if (viaHelper) return viaHelper;

    // Fallback: if it parses as a Date, force en-US output.
    const d = new Date(s);
    if (!isNaN(d)) return enUsDate.format(d);
    return s;
}

function readSession(key) {
    try {
        return sessionStorage.getItem(key) || "";
    } catch {
        return "";
    }
}
function getSupplierNameFromSession() {
    const flat = readSession("tb_supplierName") || readSession("tb_vendorId");
    if (flat) return flat.trim();
    // Fallback: flat keys may not be persisted yet — read supplier from the cached profile lookup.
    try {
        const raw = readSession("tb_lookup");
        if (!raw) return "";
        const payload = JSON.parse(raw);
        const lookup = payload?.response && typeof payload.response === "object" ? payload.response : payload;
        return String(
            lookup?.supplierName ||
            lookup?.vendorName ||
            lookup?.vendor ||
            lookup?.companyName ||
            lookup?.supplier_name ||
            lookup?.vendor_name ||
            ""
        ).trim();
    } catch {
        return "";
    }
}
function getLoggedInIdFromSession() {
    return readSession("tb_dtvid") || "";
}
function getLoggedInEmailFromSession() {
    return readSession("tb_email") || "";
}
function getRoleFromSession() {
    return readSession("tb_role");
}

/* ---------- helpers ---------- */
function pickField(obj, candidates) {
    if (!obj) return undefined;
    const map = {};
    for (const key of Object.keys(obj)) map[key.toLowerCase()] = key;
    for (const name of candidates) {
        const foundKey = map[name.toLowerCase()];
        if (foundKey && obj[foundKey] != null) return obj[foundKey];
    }
    return undefined;
}

function normalizeArray(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload === "string") {
        try {
            return normalizeArray(JSON.parse(payload));
        } catch {
            return [];
        }
    }

    // ✅ support wrapped responses { response: { personList: [...] } }
    const wrapped = payload?.response;
    if (wrapped && typeof wrapped === "object") {
        return (
            wrapped.personDetails ||
            wrapped.data ||
            wrapped.results ||
            wrapped.items ||
            wrapped.personList ||
            []
        );
    }

    return (
        payload.personDetails ||
        payload.data ||
        payload.results ||
        payload.items ||
        payload.personList ||
        payload.personDetailsList ||
        payload.personList ||
        []
    );
}

function mapHingeRows(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map((p, idx) => {
        let created_at = null;
        if (p?.createdTs) {
            const d = new Date(String(p.createdTs));
            created_at = isNaN(d) ? null : d.toISOString();
        }
        const first = p?.firstName || "";
        const last = p?.lastName || "";
        const attuid = p?.attuid || "";
        const dtvAnchorId = p?.dtvAnchorId || "";

        return {
            first_name: first,
            last_name: last,
            status: p?.status || p?.personStatus || "",
            attuidStatus: p?.attuidStatus ?? p?.attuid_status ?? null,
            createdBy: p?.nominatorName || "",
            vendor: p?.supplierName || "",
            dtvAnchorId,
            attuid,
            created_at,
            // NOTE: id is NOT npw_id for hinge rows; keep but don't treat it as NPWID
            id: dtvAnchorId || attuid || `${first}|${last}|${p?.status || ""}|${idx}`,
            _source: "hinge",
        };
    });
}

function normalizeStatusKey(s) {
    return String(s || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, "");
}

function rowKeyForSearch(r) {
    const npw =
        pickField(r, ["npw_id", "npwId", "NPW_ID", "NPWID"]) ??
        pickField(r, ["npwID", "npw_id_value"]);
    if (npw != null && String(npw).trim()) return `NPW:${String(npw).trim()}`;

    const dtv =
        pickField(r, ["dtvAnchorId", "dtvid", "dtvId", "dtv_id", "dtv_id_value"]) ??
        pickField(r, ["dtvuid", "dtvUid"]);
    if (dtv != null && String(dtv).trim()) return `DTV:${String(dtv).trim()}`;

    const att = pickField(r, ["attuid", "attUid"]);
    if (att != null && String(att).trim()) return `ATT:${String(att).trim().toUpperCase()}`;

    const email = pickField(r, ["primaryEmail", "email"]);
    if (email != null && String(email).trim()) return `EMAIL:${String(email).trim().toLowerCase()}`;

    const fallback = `${norm(r?.first_name)}|${norm(r?.last_name)}|${norm(r?.status)}`;
    return `ROW:${fallback}`;
}

function personKeyForSearch(r) {
    const dtv =
        pickField(r, ["dtvAnchorId", "dtvid", "dtvId", "dtv_id", "dtv_id_value"]) ??
        pickField(r, ["dtvuid", "dtvUid"]);
    if (dtv != null && String(dtv).trim()) return `DTV:${String(dtv).trim()}`;

    const att = pickField(r, ["attuid", "attUid"]);
    if (att != null && String(att).trim()) return `ATT:${String(att).trim().toUpperCase()}`;

    const email = pickField(r, ["primaryEmail", "email"]);
    if (email != null && String(email).trim()) return `EMAIL:${String(email).trim().toLowerCase()}`;

    return "";
}

/**
 * Merge rule (aligned with ManageRequest / ApprovalsList):
 * - If the same person exists in BOTH TB + Hinge:
 *    * If TB status is FINAL (Completed / Offboarding Completed) AND Hinge has a row -> SHOW ONLY Hinge.
 *    * Otherwise -> SHOW BOTH rows (TB and Hinge).
 * - Still de-dupe true duplicates:
 *    * Always de-dupe by rowKeyForSearch()
 *    * De-dupe Hinge↔Hinge repeats by personKeyForSearch()
 */
function mergeTbAndHingeResults(tbList, hingeList) {
    const out = [];
    const seenRow = new Set();

    const hingeSeenPerson = new Set();
    const hingeIndexByPerson = new Map();
    const tbFinalIndexByPerson = new Map();

    const FINAL_TB_STATUS = new Set(["completed", "offboardingcompleted"]);
    const isTbFinal = (r) => FINAL_TB_STATUS.has(normalizeStatusKey(r?.status || r?.personStatus));

    const isHingeRow = (r) => lower(r?._source) === "hinge" || lower(r?._source) === "hinge_api" || lower(r?._source) === "hingeapi";
    const isTbRow = (r) =>
        !isHingeRow(r) &&
        (pickField(r, ["npw_id", "npwId", "NPW_ID", "NPWID"]) != null ||
            lower(r?._source) === "tb" ||
            lower(r?._source) === "teambridge");

    const pushRow = (r) => {
        const rk = rowKeyForSearch(r);
        if (seenRow.has(rk)) return;

        const pk = personKeyForSearch(r);
        const hinge = isHingeRow(r);
        const tb = isTbRow(r);

        if (hinge && pk) {
            if (hingeSeenPerson.has(pk)) return;
            hingeSeenPerson.add(pk);

            if (tbFinalIndexByPerson.has(pk)) {
                const idx = tbFinalIndexByPerson.get(pk);
                const existing = out[idx];
                const existingRk = rowKeyForSearch(existing);
                out[idx] = r;
                seenRow.delete(existingRk);
                seenRow.add(rk);
                tbFinalIndexByPerson.delete(pk);
                hingeIndexByPerson.set(pk, idx);
                return;
            }
        }

        if (tb && pk && isTbFinal(r)) {
            if (hingeIndexByPerson.has(pk)) return;
        }

        out.push(r);
        seenRow.add(rk);

        if (hinge && pk) hingeIndexByPerson.set(pk, out.length - 1);
        if (tb && pk && isTbFinal(r)) tbFinalIndexByPerson.set(pk, out.length - 1);
    };

    for (const r of tbList || []) pushRow(r);
    for (const r of hingeList || []) pushRow(r);
    return out;
}

/* ---------- navigation flags ---------- */
function cameFromDetail() {
    try {
        return sessionStorage.getItem(FROM_SEARCH) === "1";
    } catch {
        return false;
    }
}
function clearFromDetailFlag() {
    try {
        sessionStorage.setItem(FROM_SEARCH, "0");
    } catch { }
}

function getDtvidSearch(r) {
    return (
        r._dtvid ||
        pickField(r, ["dtvAnchorId", "dtvid", "dtvId", "dtv_id_value"]) ||
        pickField(r, ["attuid"]) ||
        pickField(r, ["primaryEmail", "email"]) ||
        ""
    );
}

/* ---------- status display ---------- */
function prettyStatus(s) {
    const v = lower(s);
    if (!v) return "";
    switch (v) {
        case "active":
            return "Active";
        case "inactive":
            return "Inactive";
        case "draft":
            return "Draft";
        case "submitted":
            return "Submitted";
        case "approved":
            return "Approved";
        case "rejected":
            return "Rejected";
        case "pending":
            return "Pending";
        case "in-progress":
        case "in progress":
            return "In Progress";
        case "onleave":
        case "on leave":
        case "leave":
            return "On Leave";
        case "terminated":
            return "Terminated";
        default:
            return s;
    }
}
function statusClass(s) {
    const v = lower(s);
    switch (v) {
        case "active":
            return "status-active";
        case "inactive":
            return "status-inactive";
        case "draft":
            return "status-draft";
        case "approved":
            return "status-approved";
        case "rejected":
            return "status-rejected";
        case "submitted":
            return "status-submitted";
        case "pending":
            return "status-pending";
        case "in-progress":
        case "in progress":
        case "onleave":
        case "on leave":
        case "leave":
            return "status-inprogress";
        case "terminated":
            return "status-rejected";
        default:
            return "status-unknown";
    }
}

/* ---------- UI helpers ---------- */
function TruncCell({ value, maxChars = 8, align = "left", style }) {
    const v = norm(value) || "-";
    const short = v.length > maxChars ? v.slice(0, maxChars) : v;
    return (
        <span
            title={v}
            style={{
                display: "block",
                textAlign: align,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                ...style,
            }}
        >
            {short}
        </span>
    );
}

function EllipsisCell({ value, align = "left", style }) {
    const v = norm(value) || "-";
    return (
        <span
            title={v}
            style={{
                display: "block",
                textAlign: align,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                ...style,
            }}
        >
            {v}
        </span>
    );
}

/* ---------- id heuristics ---------- */
function isLikelyNpwId(v) {
    // NPW IDs in your system are typically numeric (bigint). Keep tolerant.
    const s = norm(v);
    if (!s) return false;
    return /^\d+$/.test(s);
}

export default function Search({ searchTerm, trigger, onClose }) {
    const location = useLocation();
    const navigate = useNavigate();

    const lookupReady = useLookupReady();

    const tbRole = (getRoleFromSession() || "").trim();
    const roleNorm = tbRole.toLowerCase();

    const cache = readCache();
    const backFromDetail = cameFromDetail();
    const cacheMatches =
        backFromDetail && Array.isArray(cache?.results) && cache?.term === norm(searchTerm);

    const initialResults = cacheMatches ? cache.results : [];
    const initialPage = cacheMatches ? readLastPageOnce() ?? cache.page ?? 1 : 1;

    const [results, setResults] = useState(initialResults);
    const [page, setPage] = useState(initialPage);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(initialResults.length > 0);
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    // ✅ Keep a stable query display so the panel does not "disappear" visually if host clears input quickly
    const [lastQuery, setLastQuery] = useState(norm(searchTerm));

    // ✅ Prevent duplicate fetches (StrictMode double-invoke / rapid trigger updates)
    const lastReqKeyRef = useRef("");
    const inFlightRef = useRef(0);

    const isCurrentRef = useRef(true);

    const persist = (obj) => {
        const prev = readCache() || {};
        const out = {
            term: obj.term ?? prev.term ?? "",
            results: Array.isArray(obj.results) ? obj.results : prev.results ?? [],
            page: Number.isFinite(obj.page) ? obj.page : prev.page ?? 1,
            pageSize: PAGE_SIZE,
            ts: Date.now(),
        };
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(out));
        } catch { }
    };

    const PASS_IDENTITY_ROLES = new Set(["nominator", "fte nominator", "cp channel delegate"]);
    const shouldPassIdentity = PASS_IDENTITY_ROLES.has(roleNorm);

    const buildTbIdentityParams = () => {
        // Special rule: Employee Sponsor search must include sponsor identity.
        // Required shape:
        // /search-api?npwId=...&action=all&role=Employee+Sponsor&loggedInUserRole=Employee+Sponsor&sponsor=<dtvid>
        if (roleNorm === "employee sponsor") {
            const dtvid = norm(getLoggedInIdFromSession());
            const role = norm(tbRole);
            const params = {
                role,
                loggedInUserRole: role,
            };
            if (dtvid) params.sponsor = dtvid;
            return params;
        }

        if (!shouldPassIdentity) return {};
        const dtvid = norm(getLoggedInIdFromSession());
        const email = norm(getLoggedInEmailFromSession());
        const role = norm(tbRole);

        const params = { role };
        if (dtvid) params.dtvid = dtvid;
        if (email) params.email = email;

        if (roleNorm === "nominator") {
            const vendor = norm(getSupplierNameFromSession());
            if (vendor) params.vendor = vendor;

            // For Nominator only: include identity params used by approvals/manage worker search.
            const searcherRoleName = role;
            const searcherSupplierName = vendor;
            if (searcherRoleName) params.searcherRoleName = searcherRoleName;
            if (searcherSupplierName) params.searcherSupplierName = searcherSupplierName;
        }
        return params;
    };

    // ✅ Now these params are sent to TeamBridge Hinge Search API (proxy)
    const buildHingeSearchParams = (q) => {
        const params = { q };
        const role = norm(tbRole);
        const vendor = norm(getSupplierNameFromSession());
        const searchDtvId = norm(getLoggedInIdFromSession());

        if (roleNorm === "nominator") {
            if (role) {
                params.role = role;
                params.searcherRoleName = role;
            }
            if (vendor) {
                params.vendor = vendor;
                params.searcherSupplierName = vendor;
            }
            if (searchDtvId) {
                params.searchDtvId = searchDtvId;
            }
            /*TODO:pentest*/ params.searchMode = "validate";
            return params;
        }

        if (roleNorm === "employee sponsor") {
            if (role) {
                params.role = role;
                params.loggedInUserRole = role;
                params.searcherRoleName = role;
            }
            if (searchDtvId) {
                params.searchDtvId = searchDtvId;
                params.sponsor = searchDtvId;
                params.approverDtvId = searchDtvId;
            }
            return params;
        }

        if (!shouldPassIdentity) {
            // Always send the caller's role for any remaining role
            // (e.g. Employee Supplier Admin, DTV OPS Admin).
            const fallbackRole = norm(tbRole);
            if (fallbackRole) {
                params.role = fallbackRole;
                params.searcherRoleName = fallbackRole;
            }
            if (searchDtvId) params.searchDtvId = searchDtvId;
            return params;
        }
        const searcherRoleName = norm(tbRole);
        const searcherSupplierName =
            roleNorm === "cp channel delegate" ? "" : norm(getSupplierNameFromSession());

        if (searcherRoleName) params.searcherRoleName = searcherRoleName;
        if (searcherSupplierName) params.searcherSupplierName = searcherSupplierName;
        if (searchDtvId) params.searchDtvId = searchDtvId;
        /*TODO:pentest*/ params.searchMode = "validate"; // special param to indicate identity validation is needed for Hinge Search API

        return params;
    };

    useEffect(() => {
        isCurrentRef.current = true;
        return () => {
            isCurrentRef.current = false;
        };
    }, []);

    useEffect(() => {
        const qRaw = String(searchTerm ?? "");
        const q = qRaw.trim();

        // cache restore when coming back from detail
        if (cacheMatches) {
            setResults(cache.results);
            setPage(initialPage);
            setHasLoaded(true);
            setLoading(false);
            setError(null);
            setLastQuery(q);
            clearFromDetailFlag();
            return;
        }
        if (backFromDetail) clearFromDetailFlag();

        // Keep previous results on screen while loading new ones (more interactive)
        setError(null);
        if (q) setLastQuery(q);

        // If empty search, reset panel but do not flicker
        if (!q) {
            setLoading(false);
            setResults([]);
            setPage(1);
            setHasLoaded(false);
            persist({ term: "", results: [], page: 1 });
            return;
        }

        // Enforce min-length across ALL search (TB + Hinge)
        if (q.length < MIN_SEARCH_CHARS) {
            toast.warn("Please enter at least 4 characters to search", {
                toastId: "min4-global-search-overlay",
                containerId: GLOBAL_TOAST_CONTAINER_ID,
            });
            setLoading(false);
            setResults([]);
            setPage(1);
            setHasLoaded(false);
            setError(null);
            persist({ term: "", results: [], page: 1 });
            return;
        }

        // Reject unsafe characters / SQL-injection-like input (mirrors backend)
        if (!isValidSearchQuery(q)) {
            toast.warn("Please avoid special characters in your search", {
                toastId: "invalid-search-overlay",
                containerId: GLOBAL_TOAST_CONTAINER_ID,
            });
            setLoading(false);
            setResults([]);
            setPage(1);
            setHasLoaded(false);
            setError(null);
            persist({ term: "", results: [], page: 1 });
            return;
        }

        // ✅ Updated config check: hinge now depends on TB_HINGE_SEARCH_URL (proxy), not direct HINGE_SEARCH_URL
        if (!TB_SEARCH_URL && !(ENABLE_HINGE && TB_HINGE_SEARCH_URL)) {
            setError("Search service is not configured.");
            setLoading(false);
            setHasLoaded(true);
            return;
        }

        // ✅ De-dupe requests: same query + role + trigger should not refetch
        const reqKey = `${q}::${roleNorm}::${String(trigger ?? "")}`;
        if (lastReqKeyRef.current === reqKey && hasLoaded) {
            return;
        }
        lastReqKeyRef.current = reqKey;

        // ✅ Debounce: prevents immediate back-to-back calls while typing or while trigger flips
        const myFlight = ++inFlightRef.current;
        const controller = new AbortController();
        const debounce = setTimeout(() => {
            if (!isCurrentRef.current) return;
            setLoading(true);

            // Wait for the lookup API to finish loading before firing any search API
            // (npw-search-api / hinge-search). Applies to every role — no role checks.
            if (!isLookupReady()) {
                toast.warn("Search will be allowed after loading the configurations. Please wait…", {
                    toastId: "search-config-loading",
                    containerId: GLOBAL_TOAST_CONTAINER_ID,
                });
                setLoading(false);
                return;
            }

            let tbArr = [];
            let hingeArr = [];

            const merge = () =>
                hingeArr.length ? (tbArr.length ? mergeTbAndHingeResults(tbArr, hingeArr) : hingeArr) : tbArr;

            const paint = (force = false) => {
                if (!isCurrentRef.current) return;
                if (myFlight !== inFlightRef.current) return;

                const merged = merge();
                if (!force && merged.length === 0) return;

                const totalPagesLocal = Math.max(1, Math.ceil(merged.length / PAGE_SIZE));
                // always reset to page 1 on new search term (avoids "empty page" confusion)
                const clampedPage = 1;

                setResults(merged);
                setPage(clampedPage);
                setHasLoaded(true);
                setLoading(false);
                persist({ term: q, results: merged, page: clampedPage });
            };

            const tbIdentityParams = buildTbIdentityParams();

            // ✅ Allow special characters: buildUrl() will safely URL-encode params
            // Note: TB API expects param name npwId; keep unchanged.
            const tbPromise = TB_SEARCH_URL
                ? tbGetJson(
                    buildUrl(TB_SEARCH_URL, { npwId: q, action: "all", ...tbIdentityParams }),
                    { fetchInit: { signal: controller.signal } }
                )
                    .then((data) => normalizeArray(data))
                    .catch((e) => {
                        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return [];
                        console.warn("[SEARCH] TB failed:", e?.message || e);
                        return [];
                    })
                : Promise.resolve([]);

            const callHinge = ENABLE_HINGE && TB_HINGE_SEARCH_URL && q.length >= Math.max(HINGE_MIN, MIN_SEARCH_CHARS);

            const hingePromise = callHinge
                ? hingeGetJson(
                    buildUrl(TB_HINGE_SEARCH_URL, buildHingeSearchParams(q)),
                    { fetchInit: { signal: controller.signal } }
                )
                    .then((data) => normalizeArray(data?.response ?? data))
                    .then(mapHingeRows)
                    .catch((e) => {
                        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return [];
                        console.warn("[SEARCH] TB Hinge search failed:", e?.message || e);
                        return [];
                    })
                : Promise.resolve([]);

            // progressive paint
            tbPromise.then((data) => {
                tbArr = data;
                paint(true);
            });
            hingePromise.then((data) => {
                hingeArr = data;
                paint(true);
            });

            const safetyTimer = setTimeout(() => paint(true), 6000);

            Promise.allSettled([tbPromise, hingePromise]).then(() => {
                clearTimeout(safetyTimer);
                paint(true);

                // If nothing came back, show "no results" instead of leaving it blank
                if (isCurrentRef.current && myFlight === inFlightRef.current) {
                    const merged = merge();
                    if (merged.length === 0) {
                        setHasLoaded(true);
                        setLoading(false);
                    }
                }
            });

            // cleanup
            return () => {
                clearTimeout(safetyTimer);
            };
        }, 250);

        return () => {
            clearTimeout(debounce);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, trigger, tbRole, lookupReady]);

    const getSortValue = (r, key) => {
        switch (key) {
            case "name":
                return `${norm(r?.first_name)} ${norm(r?.last_name)}`.toLowerCase();
            case "status":
                return (r?.status || r?.personStatus || "").toLowerCase();
            case "createdBy":
                return norm(
                    pickField(r, [
                        "submitter_dtvid",
                        "createdBy",
                        "createdby",
                        "submitter_id",
                        "nominatorName",
                    ])
                ).toLowerCase();
            case "createdOn":
                return r?.created_at ? new Date(r.created_at).getTime() : 0;
            case "vendor":
                return norm(pickField(r, ["vendor", "supplier_name", "supplierName"])).toLowerCase();
            case "attuid":
                return norm(pickField(r, ["attuid", "attUid"])).toLowerCase();
            case "attuidStatus":
                return norm(pickField(r, ["attuidStatus", "attuid_status", "attUidStatus"])).toLowerCase();
            case "dtvId":
                return norm(pickField(r, ["dtvAnchorId", "dtvid", "dtvId", "dtv_id_value"])).toLowerCase();
            default:
                return "";
        }
    };

    const sortedResults = useMemo(() => {
        if (!results.length) return [];
        const copy = [...results];
        copy.sort((a, b) => {
            const av = getSortValue(a, sortBy);
            const bv = getSortValue(b, sortBy);
            if (av === bv) return 0;
            return av < bv ? -1 : 1;
        });
        return sortDir === "desc" ? copy.reverse() : copy;
    }, [results, sortBy, sortDir]);

    const total = sortedResults.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const displayPage = Math.min(Math.max(1, page), totalPages);

    const paged = useMemo(() => {
        const start = (displayPage - 1) * PAGE_SIZE;
        return sortedResults.slice(start, start + PAGE_SIZE);
    }, [sortedResults, displayPage]);

    const goPrev = () => {
        const p = Math.max(1, displayPage - 1);
        setPage(p);
        persist({ page: p });
    };
    const goNext = () => {
        const p = Math.min(totalPages, displayPage + 1);
        setPage(p);
        persist({ page: p });
    };

    const handleSort = (key) => {
        setPage(1);
        setSortDir((prev) => (key === sortBy ? (prev === "asc" ? "desc" : "asc") : "asc"));
        setSortBy(key);
    };
    const sortArrow = (key) => (sortBy === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");

    const OPEN_BY_DTVID_STATUSES = new Set([
        "active",
        "inactive",
        "onleave",
        "on leave",
        "leave",
        "terminated",
    ]);
    const APPROVAL_GROUP = new Set(["employee sponsor", "cp channel delegate", "employee supplier admin"]);
    const MANAGE_GROUP = new Set(["fte nominator", "dtv ops admin", "nominator"]);

    // ✅ Navigate then close overlay
    const openItem = (r) => {
        const rawStatus = r?.status || r?.personStatus || "";
        const st = lower(rawStatus);
        const roleL = (tbRole || "").toLowerCase();

        const npwIdRaw = pickField(r, ["npw_id", "npwId", "NPW_ID", "NPWID"]);
        const npwId = norm(npwIdRaw);
        const dtvId = norm(getDtvidSearch(r));

        const goApprovals = APPROVAL_GROUP.has(roleL);
        const goManage = MANAGE_GROUP.has(roleL) || !goApprovals;

        let path = "";

        if (isLikelyNpwId(npwId)) {
            if (goManage && st === "draft") {
                path = `/onboarding/new/${encodeURIComponent(npwId)}`;
            } else {
                path = goManage
                    ? `/manage-requests/NPWID=${encodeURIComponent(npwId)}`
                    : `/approvals/NPWID=${encodeURIComponent(npwId)}`;
            }
        } else if (OPEN_BY_DTVID_STATUSES.has(st) && dtvId) {
            path = goApprovals
                ? `/approvals/DTVID=${encodeURIComponent(dtvId)}`
                : `/manage-requests/DTVID=${encodeURIComponent(dtvId)}`;
        } else if (dtvId) {
            path = goApprovals
                ? `/approvals/DTVID=${encodeURIComponent(dtvId)}`
                : `/manage-requests/DTVID=${encodeURIComponent(dtvId)}`;
        } else {
            console.warn("[SEARCH] openItem: no NPWID or DTVID found for row:", r);
            return;
        }

        // persist page and "came from search" so details/back can restore search
        persist({ page: displayPage });

        try {
            sessionStorage.setItem(LAST_PAGE_KEY, String(displayPage));
            sessionStorage.setItem(FROM_SEARCH, "1");

            // ✅ do NOT auto-reopen overlay on navigation
            sessionStorage.setItem(REOPEN_SEARCH, "0");

            const host =
                `${location.pathname || ""}${location.search || ""}${location.hash || ""}` || "/";
            sessionStorage.setItem(HOST_PATH, host);
            sessionStorage.setItem(RETURN_PATH, location.pathname || "/");
        } catch { }

        const before = window.location.pathname + window.location.search + window.location.hash;
        navigate(path);

        if (typeof onClose === "function") {
            try {
                onClose();
            } catch { }
        }

        setTimeout(() => {
            const after = window.location.pathname + window.location.search + window.location.hash;
            if (after === before) {
                try {
                    window.location.assign(path);
                } catch { }
            }
        }, 50);
    };

    /* ===== Layout/styles ===== */
    const colGroup = [
        <col key="name" style={{ width: "30%" }} />,
        <col key="status" style={{ width: "11%" }} />,
        <col key="attuidStatus" style={{ width: "10%" }} />,
        <col key="createdBy" style={{ width: "10%" }} />,
        <col key="createdOn" style={{ width: "10%" }} />,
        <col key="vendor" style={{ width: "9%" }} />,
        <col key="attuid" style={{ width: "8%" }} />,
        <col key="dtvid" style={{ width: "12%" }} />,
    ];

    const stickyThStyle = { position: "sticky", top: 0, background: "#fff", zIndex: 2 };
    const thName = {
        ...stickyThStyle,
        padding: "6px 10px 6px 14px",
        lineHeight: "16px",
        whiteSpace: "nowrap",
        cursor: "pointer",
    };
    const tdName = { padding: "6px 10px 6px 14px", lineHeight: "16px", verticalAlign: "middle" };

    const thCompact = { ...stickyThStyle, padding: "6px 10px", lineHeight: "16px", whiteSpace: "nowrap", cursor: "pointer" };
    const tdCompact = { padding: "6px 10px", lineHeight: "16px", verticalAlign: "middle" };

    const thTight = { ...stickyThStyle, padding: "4px 6px", lineHeight: "14px", whiteSpace: "nowrap", cursor: "pointer" };
    const tdTight = { padding: "4px 6px", lineHeight: "14px", verticalAlign: "middle" };

    const footerStyle = {
        borderTop: "1px solid rgba(0,0,0,0.12)",
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        position: "relative",
        top: "-6px",
        flexWrap: "wrap",
    };

    const showEmptyRow = !loading && hasLoaded && paged.length === 0;

    return (
        <>
            {loading && (
                <div className="loading-overlay">
                    <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>Searching…</div>
                    <div style={{ fontSize: "0.9rem", opacity: 0.8, marginTop: 6 }}>
                        Query: <span style={{ fontFamily: "monospace" }}>{lastQuery || "-"}</span>
                    </div>
                </div>
            )}

            <style>{`
        .badge.status-unknown{
          background: #FFE6C7;
          color: #7A3E00;
          border: 1px solid rgba(122,62,0,0.18);
        }
      `}</style>

            <div style={{ width: "100%" }}>
                <div className="search-results-panel" style={{ width: "100%", marginTop: "4px" }}>
                    <div className="search-header-row">
                        <div className="search-header-left">
                            <button className="btn btn-back btn-slim" onClick={onClose}>Back</button>
                        </div>

                        <div className="search-header-title">
                            Search Results
                            {lastQuery ? <span className="search-header-sub">for “{lastQuery}”</span> : null}
                        </div>

                        <div className="search-header-right">
                            {total ? `Showing ${paged.length} of ${total}` : loading ? "Searching..." : hasLoaded ? "No results" : ""}
                        </div>
                    </div>

                    {error && <div className="error">{error}</div>}
                    <hr />

                    <div className="table-wrap">
                        <table className="search-table" style={{ width: "100%", tableLayout: "fixed" }}>
                            <colgroup>{colGroup}</colgroup>

                            <thead>
                                <tr>
                                    <th style={thName} onClick={() => handleSort("name")}>Name{sortArrow("name")}</th>
                                    <th style={thCompact} onClick={() => handleSort("status")}>DTVID Status{sortArrow("status")}</th>
                                    <th className="col-hide-md" style={thTight} onClick={() => handleSort("attuidStatus")}>ATTUID Status{sortArrow("attuidStatus")}</th>
                                    <th className="col-hide-sm" style={thTight} onClick={() => handleSort("createdBy")}>Created by{sortArrow("createdBy")}</th>
                                    <th className="col-hide-sm" style={thTight} onClick={() => handleSort("createdOn")}>Created on{sortArrow("createdOn")}</th>
                                    <th className="col-hide-md" style={thTight} onClick={() => handleSort("vendor")}>Vendor{sortArrow("vendor")}</th>
                                    <th className="col-hide-md" style={thTight} onClick={() => handleSort("attuid")}>ATTUID{sortArrow("attuid")}</th>
                                    <th style={thCompact} onClick={() => handleSort("dtvId")}>DTVID{sortArrow("dtvId")}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paged.map((r, i) => {
                                    const createdStr = r?.created_at ? formatMmddyyyy(r.created_at) : "-";
                                    const rawDtvStatus = r?.status || r?.personStatus || "";
                                    const dtvPillText = prettyStatus(rawDtvStatus);
                                    const rawAttuidStatus =
                                        pickField(r, ["attuidStatus", "attuid_status", "attUidStatus"]) ?? null;
                                    const attuidPillText = prettyStatus(rawAttuidStatus) || "Not Assigned";

                                    const createdBy =
                                        pickField(r, ["submitter_dtvid", "createdBy", "createdby", "submitter_id", "nominatorName"]) || "-";
                                    const vendor = pickField(r, ["vendor", "supplier_name", "supplierName"]) || "-";
                                    const attuid = pickField(r, ["attuid", "attUid"]) || "-";
                                    const dtvIdCell = pickField(r, ["dtvAnchorId", "dtvid", "dtvId", "dtv_id_value"]) || "-";

                                    const rowKey =
                                        pickField(r, ["npw_id", "npwId"]) ||
                                        pickField(r, ["dtvAnchorId", "dtvid", "dtvId"]) ||
                                        `${i}-${attuid}-${dtvIdCell}`;

                                    const fullName = `${norm(r?.first_name)} ${norm(r?.last_name)}`.trim();
                                    const nameShort = fullName.length > 35 ? fullName.slice(0, 35) : fullName;

                                    const dtvIdVal = norm(dtvIdCell) || "-";
                                    const dtvIdShort = dtvIdVal.length > 10 ? dtvIdVal.slice(0, 10) : dtvIdVal;

                                    const statusVal = norm(dtvPillText || rawDtvStatus) || "-";
                                    const statusShort = statusVal.length > 10 ? statusVal.slice(0, 10) : statusVal;
                                    const attuidStatusVal = norm(attuidPillText) || "Not Assigned";
                                    const attuidStatusShort = attuidStatusVal.length > 12 ? attuidStatusVal.slice(0, 12) : attuidStatusVal;

                                    return (
                                        <tr key={rowKey}>
                                            <td style={tdName}>
                                                <button
                                                    type="button"
                                                    className="link-button"
                                                    onClick={() => openItem(r)}
                                                    title={fullName || "-"}
                                                    style={{
                                                        display: "block",
                                                        maxWidth: "100%",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    {nameShort || "-"}
                                                </button>
                                            </td>

                                            <td style={tdCompact}>
                                                <span
                                                    className={`badge ${statusClass(rawDtvStatus)}`}
                                                    title={statusVal}
                                                    style={{
                                                        display: "inline-block",
                                                        maxWidth: "100%",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        verticalAlign: "middle",
                                                    }}
                                                >
                                                    {statusShort}
                                                </span>
                                            </td>

                                            <td className="col-hide-md" style={tdTight}>
                                                <span
                                                    className={`badge ${statusClass(rawAttuidStatus || "")}`}
                                                    title={attuidStatusVal}
                                                    style={{
                                                        display: "inline-block",
                                                        maxWidth: "100%",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        verticalAlign: "middle",
                                                    }}
                                                >
                                                    {attuidStatusShort}
                                                </span>
                                            </td>

                                            <td className="col-hide-sm" style={tdTight}><TruncCell value={createdBy} maxChars={8} /></td>
                                            <td className="col-hide-sm" style={tdTight}><EllipsisCell value={createdStr} /></td>
                                            <td className="col-hide-md" style={tdTight}><TruncCell value={vendor} maxChars={8} /></td>
                                            <td className="col-hide-md" style={tdTight}><EllipsisCell value={attuid} align="center" /></td>

                                            <td style={tdCompact}>
                                                <span
                                                    title={dtvIdVal}
                                                    style={{
                                                        display: "block",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {dtvIdShort}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {showEmptyRow && (
                                    <tr>
                                        <td colSpan={8} className="no-results">No results found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div style={footerStyle}>
                            <button className="linklike" onClick={goPrev} disabled={displayPage === 1}>Prev</button>
                            <span>Page {displayPage} / {totalPages}</span>
                            <button className="linklike" onClick={goNext} disabled={displayPage === totalPages}>Next</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
